import get from 'lodash.get';
import EnumHelper from 'utilities/enumHelper';
import moment from 'moment';

export default (profile, mappingOptions) => {
  const label = EnumHelper.valueToOptionLabel;
  const createGetter = object => ({
    value: path => get(object, path, ''),
    option: (options, path) => label(options, get(object, path, 0)),
    map: path => get(object, path, []) || [],
    resultValue: (title, path, transform = null) => {
      const value = get(object, path, '');
      const transformedValue = transform && value ? transform(value) : value;
      return value ? [title, transformedValue.toString()] : null;
    },
    resultOption: (title, options, path) => {
      const value = label(options, get(object, path, 0));
      return value ? [title, value.toString()] : null;
    },
  });

  const { gender, ethnicity, maritalStatus, residentStatus } = mappingOptions;
  const { contactType, relationship: relations } = mappingOptions;
  const getProfile = createGetter(profile);
  const formatDate = date => moment.utc(date).format('MM/DD/YYYY');
  const idType = (((profile || {}).basicInfo || {}).idType || '').toUpperCase();

  const basicInfo = [
    {
      data: [
        getProfile.resultValue('Title', 'basicInfo.title'),
        getProfile.resultValue('First Name', 'basicInfo.firstName'),
        getProfile.resultValue('Middle Initial', 'basicInfo.middleInitial'),
        getProfile.resultValue('Last Name', 'basicInfo.lastName'),
        getProfile.resultValue('Employee ID', 'basicInfo.employeeId'),
        getProfile.resultValue(
          idType === 'SIN'
            ? 'Social Insurance Number'
            : 'Social Security Number',
          'basicInfo.socialSecurityMask',
        ),
        getProfile.resultValue('Tax ID', 'basicInfo.taxId'),
        getProfile.resultValue(
          'Date of Birth',
          'basicInfo.birthDate',
          formatDate,
        ),
        getProfile.resultOption('Gender', gender, 'basicInfo.gender'),
        getProfile.resultOption('Ethnicity', ethnicity, 'basicInfo.'),
        getProfile.resultOption('Marital Status', maritalStatus, 'basicInfo.'),
        getProfile.resultOption(
          'Resident Status',
          residentStatus,
          'basicInfo.residentStatus',
        ),
        getProfile.resultValue(
          'Country of Residence',
          'basicInfo.countryofResidence',
        ),
        getProfile.resultValue('Nationality', 'basicInfo.nationality'),
      ].filter(Boolean),
    },
  ];

  const addresses = getProfile.map('addresses').map(address => {
    const getAddress = createGetter(address);
    const title = `${getAddress.option(contactType, 'typeofContact')} Address`;
    const data = [
      getAddress.resultValue('First Name', 'firstName'),
      getAddress.resultValue('Middle Initial', 'middleInitial'),
      getAddress.resultValue('Last Name', 'lastName'),
      getAddress.resultValue('Company', 'company'),
      getAddress.resultValue('Address Line 1', 'addressLine1'),
      getAddress.resultValue('Address Line 2', 'addressLine2'),
      getAddress.resultValue('Address Line 3', 'addressLine3'),
      getAddress.resultValue('City', 'city'),
      getAddress.resultValue('County', 'county'),
      getAddress.resultValue('Tax County', 'taxCounty'),
      getAddress.resultValue('State', 'state'),
      getAddress.resultValue('Zip Code', 'zipCode'),
      getAddress.resultValue('Country', 'country'),
    ].filter(Boolean);
    return { title, data };
  });

  const emailAddresses = getProfile.map('emailAddresses');
  const emails = emailAddresses.length && [
    {
      data: emailAddresses.map(email => {
        const getEmail = createGetter(email);
        const name = `${getEmail.option(
          contactType,
          'typeofContact',
        )} Email Address`;
        const value = getEmail.value('emailAddress');
        return [name, value];
      }),
    },
  ];

  const phoneNumbers = getProfile.map('phoneNumbers');
  const phones = phoneNumbers.length && [
    {
      data: phoneNumbers.map(phone => {
        const getPhone = createGetter(phone);
        const name = `${getPhone.option(
          contactType,
          'typeofContact',
        )} Phone Number`;
        const code = getPhone.value('countryCode');
        const ext = getPhone.value('extension');
        const value = [
          code && `+${code}`,
          getPhone.value('cell'),
          ext && `ext=${ext}`,
        ]
          .filter(Boolean)
          .join(' ');
        return [name, value];
      }),
    },
  ];

  const deposits = getProfile.map('directDeposits').map(deposit => {
    const getDeposit = createGetter(deposit);
    const percentage = value => `${value}%`;
    const data = [
      getDeposit.resultValue('Routing Number', 'routingNumber'),
      getDeposit.resultValue('Bank Account Number', 'bankAccountNumber'),
      getDeposit.resultValue('Is Direct Deposit', 'isDirectDeposit'),
      getDeposit.resultValue('Flat Amount', 'flatAmount'),
      getDeposit.resultValue('Percent Rate', 'percentRate', percentage),
      getDeposit.resultValue('Split Percent', 'splitPercentage', percentage),
    ].filter(Boolean);
    return { data };
  });

  const unions = getProfile.map('unions').map(union => {
    const getUnion = createGetter(union);
    return {
      data: [
        getUnion.resultValue('Union ID', 'unionId'),
        getUnion.resultValue('Member Type', 'memberType'),
        getUnion.resultValue('Is Locked Out', 'isLockedOut'),
      ].filter(Boolean),
    };
  });

  const contacts = getProfile.map('alternativeContacts').map(contact => {
    const getContact = createGetter(contact);
    const title = `${getContact.option(contactType, 'typeofContact')} Contact`;
    const data = [
      getContact.resultValue('Full Name', 'basicInfo.name'),
      getContact.resultValue('Address', 'addresses[0].addressLine1'),
      getContact.resultValue('City', 'addresses[0].city'),
      getContact.resultValue('State', 'addresses[0].state'),
      getContact.resultValue('Zip Code', 'addresses[0].zipCode'),
    ].filter(Boolean);
    getContact.map('phoneNumbers').forEach(phone => {
      const getPhone = createGetter(phone);
      const name = `${getPhone.option(
        contactType,
        'typeofContact',
      )} Phone Number`;
      const code = getPhone.value('countryCode');
      const ext = getPhone.value('extension');
      const value = [
        code && `+${code}`,
        getPhone.value('cell'),
        ext && `ext=${ext}`,
      ]
        .filter(Boolean)
        .join(' ');
      data.push([name, value]);
    });
    const relationship = getContact.resultOption(
      'Relationship',
      relations,
      'relationship',
    );
    if (relationship) data.push(relationship);
    return { title, data };
  });

  const corporateAddress = [];
  if (getProfile.value('loanout.addressLine1')) {
    corporateAddress.push(getProfile.value('loanout.addressLine1'));
    corporateAddress.push(getProfile.value('loanout.city'));
    corporateAddress.push(',');
    corporateAddress.push(getProfile.value('loanout.state'));
    corporateAddress.push(getProfile.value('loanout.zipCode'));
  }
  const loanout = getProfile.value('loanout') && [
    {
      data: [
        getProfile.resultValue('Tax-ID or FEIN', 'loanout.taxIdNumber'),
        getProfile.resultValue('Company Name', 'loanout.name'),
        getProfile.resultValue('Company Type', 'loanout.corpType'),
        ['Corporation Address', corporateAddress.join(' ')],
      ].filter(Boolean),
    },
  ];

  return {
    basicInfo,
    addresses,
    deposits,
    contacts,
    emails,
    phones,
    unions,
    loanout,
  };
};
